
import { SERVICEPOINT_TYPE, SOURCE_SYSTEM } from "@/constans";
import {
  Adapter,
  Channel,
  IAdapter,
  IChannel,
  IChannelData,
  IClient,
  IClientData,
  IClientParams,
  ICountry,
  ICountryConfig,
  ICountryConfigResponse,
  ICurrency,
  ILimit,
  IProsegurPay,
  IServicepointData,
  IServicepointDetail,
  IServicepointParams,
  ISubclient,
  ISubclientData,
  ISubclientParams,
  IThirdcode,
  IUpdateServicepoint,
  IUser,
} from "@/entities";
import { i18n } from "@/i18n";
import {
  IGenericResponse,
  IPageRequest,
  IPageResponse,
  IValidateSolServicepointParams,
  IValidateSolServicepointResponse,
} from "@/services";
import { debounce } from "lodash";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import Multiselect from "vue-multiselect";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import ThirdcodeTable from "@/components/thirdcode/ThirdcodeTable.vue";
import ProsegurPay from "@/components/prosegurpay/ProsegurPay.vue";
import UsersOnFlowApprove from "@/components/approvals/UsersOnFlowApprove.vue";
import { IUserServicepointApproval } from "@/entities/servicepoint_approval";
import AsyncComputed from "vue-async-computed-decorator";

@Component({
  name: "servicepoint-add-page",
  components: {
    ValidationObserver,
    Multiselect,
    ThirdcodeTable,
    ProsegurPay,
    UsersOnFlowApprove,
  },
})
export default class ServicepointAddPage extends Vue {
  @Getter("getCountryConfig") countryConfig!: ICountryConfig;
  @Action("fetchClientsPage") fetchClientsPage!: (
    params: IClientParams
  ) => Promise<IPageResponse<IClientData> | undefined>;
  @Action("fetchSubclientsPage") fetchSubclientsPage!: (
    params: ISubclientParams
  ) => Promise<IPageResponse<ISubclientData> | undefined>;
  @Action("fetchServicepointsPage") fetchServicepointsPage!: (
    params: IServicepointParams
  ) => Promise<IPageResponse<IServicepointData> | undefined>;
  @Action("fetchChannelsPage") fetchChannelsPage!: ({
    pageRequest,
    text,
    countryId,
  }: {
    pageRequest: IPageRequest;
    text?: string;
    countryId?: number;
  }) => Promise<IPageResponse<IChannelData> | undefined>;
  @Action("createChannel") createChannel!: ({
    text,
    countryId,
  }: {
    text?: string;
    countryId?: number;
  }) => Promise<IGenericResponse<IChannelData> | undefined>;
  @Action("saveUpdateServicepoint") saveUpdateServicepoint!: (
    updateServicepoint: IUpdateServicepoint
  ) => Promise<IGenericResponse<IServicepointDetail> | undefined>;
  @Action("validateSolServicepoint") validateSolServicepoint!: (payload: {
    countryCode: string;
    params: IValidateSolServicepointParams;
  }) => Promise<IValidateSolServicepointResponse | undefined>;

  @Getter("getLoggedUser") loggedUser?: IUser;
  @Getter("getCountriesPage") countriesPage?: IPageResponse<ICountry>;
  @Getter("getAssignableAdapters") assignableAdapters!: IAdapter[];
  @Getter("getCurrenciesPage") currenciesPage?: IPageResponse<ICurrency>;

  private clientsPage: IClient[] = [];
  private subclientsPage: ISubclient[] = [];
  public channels: IChannel[] = [];

  // SOL Validation form
  public validateInSolInput = true;
  public countrySelected: ICountry | null = null;
  public sourceSystemSelected: string | null = null;
  public clientSelected: IClient | null = null;
  public subclientSelected: ISubclient | null = null;
  public clientPreciseSearch = false;
  public subclientPreciseSearch = false;
  public codeSolInput: string | null = null;
  private solServicePointExists = false;

  // Creation fields
  public prosegurPayInput: IProsegurPay | null = null;
  private type?: SERVICEPOINT_TYPE = SERVICEPOINT_TYPE.SOL;
  private codeSol: string | null = null;
  private oidSolInput?: string | null = null;
  public idSmartcashInput?: string | null = null;
  public deviceIdInput?: string | null = null;
  public descriptionInput?: string | null = null;
  public addressInput?: string | null = null;
  public openChannelInput?: boolean | null = null;
  public accountDateInput?: boolean | null = null;
  public assignableAdaptersFinal: IAdapter[] = [];
  public adaptersSelect: IAdapter[] = [];
  public currenciesSelect: ICurrency[] = [];
  public channelsSelect: IChannel[] = [];
  public visibleCountry = true;
  private codTypeServicepoint?: string;
  private descTypeServicePoint?: string;
  public ssIdInput?: string | null = null;
  public passwordInput?: string | null = null;
  public thirdcodeList?: IThirdcode[] = [];
  public limitList: ILimit[] = [];
  public agilePickup = false;
  public econoCash = false;
  public enabledReadQr = false;
  public enabledFlowApproval = false;
  public usersApprovalList: IUserServicepointApproval[] = [];

  constructor() {
    super();
  }

  get typeSOL(): boolean {
    return this.type === SERVICEPOINT_TYPE.SOL;
  }

  set typeSOL(value: boolean) {
    this.type = value ? SERVICEPOINT_TYPE.SOL : SERVICEPOINT_TYPE.CASHTODAY;
  }

  get clientOptions(): IClient[] {
    return this.clientsPage.map((c) => {
      if (c.codeSol) {
        c.label = c.codeSol + " - " + c.description;
      } else {
        c.label = c.description;
      }
      return c;
    });
  }
  get subclientOptions(): ISubclient[] {
    return this.subclientsPage.map((s) => {
      if (s.codeSol) {
        s.label = s.codeSol + " - " + s.description;
      } else {
        s.label = s.description;
      }
      return s;
    });
  }
  get isCodeDisabled(): boolean {
    if (this.validateInSolInput) {
      return (
        !this.countrySelected ||
        !this.clientSelected ||
        !this.subclientSelected ||
        !this.sourceSystemSelected
      );
    } else {
      return !this.countrySelected || !this.clientSelected || !this.subclientSelected;
    }
  }

  get countryId(): number | undefined {
    return this.countrySelected?.id;
  }

  @AsyncComputed()
  async config(): Promise<ICountryConfigResponse | undefined> {
    if (this.countrySelected?.code) {
      return this.$services.country.getCountryConfigByCode(this.countrySelected?.code);
    }
    return {};
  }

  get showAdditionalFields(): boolean {
    const result = this.codeSolInput !== null && this.codeSolInput !== "";
    if (this.validateInSolInput) {
      return result && this.solServicePointExists;
    }

    return result;
  }

  get showAgilePickupOption(): boolean {
    return this.adaptersSelect.findIndex((adapter) => adapter.code === "pickup") !== -1;
  }
  get isDepositModule(): boolean {
    return this.adaptersSelect.findIndex((adapter) => adapter.code === "deposit") !== -1;
  }
  get isRVModule(): boolean {
    return this.adaptersSelect.findIndex((adapter) => adapter.code === "rv") !== -1;
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  async mounted(): Promise<void> {
    extend("min", {
      validate(value, { length }) {
        return value.length >= length;
      },
      params: ["length"],
      message: i18n.t("validation.messages.min").toString(),
    });

    extend("servicepoint_exists", {
      validate: async () => {
        let countryValid;
        if (this.loggedUser?.admin) {
          countryValid = (
            await (this.$refs.country as InstanceType<typeof ValidationProvider>).validate()
          ).valid;
        }
        const sourceSystemValid = (
          await (this.$refs.sourceSystem as InstanceType<typeof ValidationProvider>).validate()
        ).valid;
        const clientValid = (
          await (this.$refs.client as InstanceType<typeof ValidationProvider>).validate()
        ).valid;
        const subClientValid = (
          await (this.$refs.subclient as InstanceType<typeof ValidationProvider>).validate()
        ).valid;

        if (this.loggedUser?.admin) {
          return countryValid && sourceSystemValid && clientValid && subClientValid
            ? this.validateCode()
            : true;
        } else {
          return sourceSystemValid && clientValid && subClientValid ? this.validateCode() : true;
        }
      },
      message: () => this.$t("validation.messages.servicepointNotFound") as string,
    });

    await this.checkAdapters();
  }

  debouncedSearchClients = debounce(
    async (description?: string) => this.searchClients(description),
    500
  );

  async searchClients(description?: string): Promise<void> {
    this.subclientSelected = null;
    this.subclientsPage = [];
    this.clientsPage = [];
    const normalizedDescription = description?.trim() || undefined;

    if (this.countrySelected) {
      const params: IClientParams = {
        page: 0,
        size: 10,
        countryId: this.countrySelected?.id,
        codeOrDesc: normalizedDescription,
        hasOid: this.validateInSolInput ? true : undefined,
      };
      if (normalizedDescription && this.clientPreciseSearch) {
        params.codeOrDescPrecise = true;
      }
      const res = await this.fetchClientsPage(params);

      if (res) {
        this.clientsPage = res.content;
      }
    }
  }

  debouncedSearchCentros = debounce(
    async (description?: string) => this.searchCentros(description),
    500
  );

  async searchCentros(description?: string): Promise<void> {
    const normalizedDescription = description?.trim() || undefined;
    const params: ISubclientParams = {
      page: 0,
      size: 10,
      clientId: this.clientSelected?.id,
      codeOrDesc: normalizedDescription,
      hasOid: this.validateInSolInput ? true : undefined,
    };
    if (normalizedDescription && this.subclientPreciseSearch) {
      params.codeOrDescPrecise = true;
    }
    const res = await this.fetchSubclientsPage(params);

    if (!res) {
      this.subclientsPage = [];
    } else {
      this.subclientsPage = res.content;
    }
  }

  debouncedSearchChannels = debounce(async (text?: string) => this.searchChannels(text), 500);

  async searchChannels(text?: string): Promise<void> {
    let countryId = this.countrySelected?.id;
    const normalizedText = text?.trim() || undefined;

    const res = await this.fetchChannelsPage({
      pageRequest: {
        page: 0,
        size: 10,
        sort: "text,ASC",
      },
      text: normalizedText,
      countryId,
    });

    this.channels = res?.content.map((data) => new Channel(data)) || [];
  }

  debouncedCreateChannel = debounce(async (text: string) => this.createChannelProcess(text), 1000);

  async createChannelProcess(text: string): Promise<void> {
    const res = await this.createChannel({
      text: text,
      countryId: this.countrySelected?.id,
    });

    if (res?.status === 1) {
      this.makeToast("success", this.$t(res.message) as string);

      await this.searchChannels();
      this.channelsSelect.push(res.details);
    } else if (res?.status === 0) {
      this.makeToast("danger", this.$t(res.message) as string);

      await this.searchChannels();
    }
  }

  get optionsCountries(): ICountry[] {
    if (this.loggedUser?.admin) {
      this.visibleCountry = true;
      return this.countriesPage?.content || [];
    } else if (this.loggedUser?.country?.id) {
      this.visibleCountry = false;
      this.countrySelected = this.loggedUser.country;
      this.searchClients();

      return (
        this.countriesPage?.content.filter(
          (country) => this.loggedUser?.country?.id === country.id
        ) || []
      );
    }
    return [];
  }

  get optionsSourceSystem(): any {
    return [
      i18n.t("label.none"),
      SOURCE_SYSTEM.SIGII,
      SOURCE_SYSTEM.GENESIS,
      SOURCE_SYSTEM.CORPOINT,
      SOURCE_SYSTEM.PROFAT,
    ];
  }

  async onSubmit(): Promise<void> {
    const updateServicepoint: IUpdateServicepoint = {
      type: this.type!,
      oidSol: this.oidSolInput || "",
      codeSol: this.validateInSolInput ? this.codeSol! : this.codeSolInput!,
      uniqueCode: "",
      idSmartcash: this.idSmartcashInput || undefined,
      deviceId: this.deviceIdInput || undefined,
      description: this.descriptionInput!,
      address: this.addressInput!,
      openChannel: this.openChannelInput!,
      accountDate: this.accountDateInput!,
      active: true,
      subclientId: this.subclientSelected!.id!,
      ssId: this.ssIdInput || "",
      password: this.passwordInput || "",
      adapters: this.adaptersSelect.map((adapter) => ({
        id: adapter.id || 0,
      })),
      channels: this.channelsSelect.map((channel) => ({
        id: channel.id || 0,
      })),
      currencies: this.currenciesSelect.map((currency) => ({
        id: currency.id || 0,
      })),
      codTypeServicepoint: this.codTypeServicepoint,
      descTypeServicePoint: this.descTypeServicePoint,
      thirdcodes: this.thirdcodeList,
      limits: this.limitList,
      agilePickup: this.agilePickup,
      econoCash: this.econoCash,
      prosegurPay: this.getProsegurPayData(),
      enabledReadQr: this.enabledReadQr,
      enabledFlowApproval: this.enabledFlowApproval,
      userApprovals: this.getUsersApprovalData(),
      countryId: this.countrySelected?.id,
    };

    const res = await this.saveUpdateServicepoint(updateServicepoint);

    if (res?.status === 1) {
      await this.fetchServicepointsPage({
        size: 10,
        page: 0,
        subclientId: undefined,
        active: undefined,
      });

      this.$router.back();
    }
  }

  async validateCode(): Promise<boolean> {
    let res;

    if (
      this.sourceSystemSelected === SOURCE_SYSTEM.SIGII ||
      this.sourceSystemSelected === SOURCE_SYSTEM.GENESIS ||
      this.sourceSystemSelected === SOURCE_SYSTEM.CORPOINT ||
      this.sourceSystemSelected === SOURCE_SYSTEM.PROFAT
    ) {
      res = await this.validateSolServicepoint({
        countryCode: this.countrySelected!.code!,
        params: {
          servicePointCode: this.codeSolInput!,
          sourceSystem: this.sourceSystemSelected,
        },
      });
    } else {
      res = await this.validateSolServicepoint({
        countryCode: this.countrySelected!.code!,
        params: {
          clientCode: this.clientSelected?.codeSol,
          centerCode: this.subclientSelected!.codeSol!,
          servicePointCode: this.codeSolInput!,
          sourceSystem: this.sourceSystemSelected!,
        },
      });
    }

    this.solServicePointExists = res ? res.exito : false;

    if (res?.exito) {
      this.codeSol = res?.punto.codPunto;
      this.oidSolInput = res?.punto.idPunto;
      this.descriptionInput = res?.punto.desPunto;
      this.addressInput = res?.punto.direccion?.desDireccionVia;
      this.codTypeServicepoint = res?.punto.codTipoPunto;
      this.descTypeServicePoint = res?.punto.desTipoPunto;
      this.thirdcodeList = res.punto.codigosTercero?.map((e) => {
        return {
          system: e.sistemaTerceroCodigo,
          code: e.valor,
        } as IThirdcode;
      });
      return true;
    } else {
      this.codeSol = null;
      this.oidSolInput = null;
      this.descriptionInput = null;
      this.addressInput = null;
      this.codTypeServicepoint = undefined;
      this.descTypeServicePoint = undefined;
      this.thirdcodeList = [];
      return false;
    }
  }

  async validateInSolInputChange(checked: boolean): Promise<void> {
    if (!checked) {
      this.typeSOL = false;
    }

    this.sourceSystemSelected = null;
    this.codeSolInput = null;
    this.codeSol = null;
    this.descriptionInput = null;
    this.addressInput = null;
    this.deviceIdInput = null;
    this.idSmartcashInput = null;
    this.openChannelInput = null;
    this.accountDateInput = null;
    this.adaptersSelect = [];
    this.currenciesSelect = [];
    this.channelsSelect = [];
    this.ssIdInput = null;
    this.passwordInput = null;

    this.solServicePointExists = false;
    this.codTypeServicepoint = undefined;
    this.descTypeServicePoint = undefined;
    this.thirdcodeList = [];
    this.limitList = [];
    await this.checkAdapters();
  }

  async countrySelectedChange(): Promise<void> {
    // Reset same fields
    this.validateInSolInputChange(this.validateInSolInput);
    this.clientSelected = null;
    this.subclientSelected = null;

    await Promise.all([this.searchChannels(), this.searchClients()]);
  }

  codeSolInputChange(): void {
    this.codeSol = null;
    this.descriptionInput = null;
    this.addressInput = null;
    this.deviceIdInput = null;
    this.idSmartcashInput = null;
    this.openChannelInput = null;
    this.accountDateInput = null;
    this.adaptersSelect = [];
    this.currenciesSelect = [];
    this.channelsSelect = [];
    this.ssIdInput = null;
    this.passwordInput = null;
    this.codTypeServicepoint = undefined;
    this.descTypeServicePoint = undefined;
    this.thirdcodeList = [];
    this.limitList = [];
  }

  async clientSelectedChange(): Promise<void> {
    this.subclientSelected = null;
    this.sourceSystemSelected = null;
    this.codeSolInput = null;

    this.codeSolInputChange();

    await this.debouncedSearchCentros();
  }

  adaptersSelected(selectedOption: Adapter): void {
    if (selectedOption.code === "deposit") {
      if (this.mainCurrency) {
        this.currenciesSelect.push(this.mainCurrency);
      }
    }
    if (selectedOption.code === "rv") {
      this.ssIdInput = "Cash Today Fit";
      this.passwordInput = "ProsegurC@sh1000";
    }
  }

  get mainCurrency(): ICurrency | undefined {
    const mainCurrencyCode = this.countrySelected?.mainCurrency?.code;

    return mainCurrencyCode
      ? this.currenciesPage?.content.find((e) => e.code == mainCurrencyCode)
      : undefined;
  }

  adaptersRemoved(removedOption: Adapter): void {
    if (removedOption.code === "deposit") {
      this.currenciesSelect = [];
    }
    if (removedOption.code === "rv") {
      this.ssIdInput = null;
      this.passwordInput = null;
    }
    if (removedOption.code === "pickup") {
      this.agilePickup = false;
    }
  }

  async toggleCheckbox(): Promise<void> {
    if (this.typeSOL) {
      this.deviceIdInput = null;
      this.idSmartcashInput = null;
      this.openChannelInput = null;
      this.accountDateInput = null;
      this.adaptersSelect = [];
      this.currenciesSelect = [];
      this.channelsSelect = [];
      this.ssIdInput = null;
      this.passwordInput = null;
    } else {
      this.adaptersSelect = [];
    }
    await this.checkAdapters();
  }

  async checkAdapters(): Promise<void> {
    this.assignableAdaptersFinal = [];
    this.assignableAdaptersFinal = this.assignableAdapters.filter((adapter) =>
      this.filterAdapter(adapter)
    );
  }

  filterAdapter(adapter: IAdapter): boolean | undefined {
    if (this.typeSOL) {
      return adapter.name?.includes("Sol Customer");
    } else {
      return adapter.name?.includes("Smartcash") || adapter.name?.includes("Prosegur Pay");
    }
  }

  async toggleUnSelectCurrency(currency: ICurrency): Promise<void> {
    if (currency.code === this.mainCurrency) {
      this.$nextTick(() => {
        this.currenciesSelect.unshift(currency);
      });
    }
  }

  onHandleProsegurPayComplete(data: IProsegurPay): void {
    this.prosegurPayInput = data;
  }

  get isProsegurPayModule(): boolean {
    return this.adaptersSelect.findIndex((adapter) => adapter.code === "prosegurpay") !== -1;
  }

  getProsegurPayData(): IProsegurPay {
    if (this.isProsegurPayModule && this.prosegurPayInput) {
      const data = this.prosegurPayInput;
      return {
        accountId: data.accountId,
        locationId: data.locationId,
        fee: data.fee,
      } as IProsegurPay;
    } else {
      return this.resetProsegurPayData();
    }
  }

  resetProsegurPayData(): IProsegurPay {
    this.prosegurPayInput = null;
    return {
      accountId: "",
      locationId: "",
      fee: 0,
    } as IProsegurPay;
  }

  handleAddUserApproval(user: IUserServicepointApproval): void {
    const isDuplicate = this.usersApprovalList.find((existingUser) => existingUser.id === user.id);

    if (!isDuplicate) {
      this.usersApprovalList.push(user);
    }
  }

  handleRemoveUserApproval(user: IUserServicepointApproval): void {
    this.usersApprovalList = this.usersApprovalList.filter(
      (existingUser) => existingUser.id !== user.id
    );
  }

  getUsersApprovalData(): number[] {
    if (this.enabledFlowApproval) {
      return this.usersApprovalList.map((user) => user.id);
    } else {
      return [];
    }
  }
}
